import { getBaseQueryForGoLang, getBaseUrl } from 'src/utils/baseQuery';
import {
  createApi,
  TypedUseQueryStateResult,
} from '@reduxjs/toolkit/query/react';
import { User } from 'src/models/IAuth';
import { setUser } from 'src/redux/Auth';
import {
  EventStreamContentType,
  fetchEventSource,
} from '@microsoft/fetch-event-source';
import { PagListResult } from 'src/models/types';
import { Notification, TARGET_NOTIFICATION } from 'src/models/notifications';
import { GoPagList } from 'src/models/types';
import {
  setNewNotification,
  setNotifications,
} from 'src/redux/slices/notificationsSlice';
import { onOrderClose, onOrderOpen } from '../../redux/slices/ordersSlce';
import { goModulesSlice } from '../settings/goModels/goModels';
import { ThunkAction } from '@reduxjs/toolkit';
import { getAuthTokens } from '../../utils/helpers/authHelper';

export class RetriableError extends Error {}
export class FatalError extends Error {}
export enum EventKeysEnum {
  NewOrder = 'order-created',
  OpenOrder = 'order-open',
  CloseOrder = 'order-close',
  UserLogin = 'user-login',
  Notification = 'send_notification',
  Counterparty = 'counterparty-balance',
}

export const goUsers = createApi({
  reducerPath: 'goUsers',
  baseQuery: getBaseQueryForGoLang,
  tagTypes: ['User'],
  endpoints: (builder) => ({
    getUsers: builder.query<GoPagList<User>, any>({
      query: (params) => ({
        url: 'users',
        method: 'GET',
        params: params,
      }),
      providesTags: ['User'],
    }),
    getNotifications: builder.query<PagListResult<Notification>, unknown>({
      query: () => ({
        url: 'notifications',
        method: 'GET',
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled
          .then((res) => dispatch(setNotifications(res?.data?.data)))
          .catch(() => true);
      },
    }),
    getUser: builder.query<{ data: User }, any>({
      query: () => ({
        url: 'users/personal/',
        method: 'GET',
      }),
      providesTags: ['User'],
      onQueryStarted: async (_, api: any) => {
        const { dispatch, queryFulfilled } = api;
        const { data } = await queryFulfilled;
        dispatch(setUser(data?.data));
      },
      async onCacheEntryAdded(_, { dispatch, getCacheEntry, getState }) {
        const token = getAuthTokens();

        await fetchEventSource(
          `${getBaseUrl(true)}sse/company-subscribe`,

          {
            headers: {
              Authorization: `Bearer ${token.accessToken}`,
            },

            async onopen(response) {
              if (
                response.ok &&
                response.headers.get('content-type') === EventStreamContentType
              ) {
                return;
              } else if (
                response.status >= 400 &&
                response.status < 500 &&
                response.status !== 429
              ) {
                throw new FatalError();
              } else {
                throw new RetriableError();
              }
            },
            onmessage(ev) {
              const userId = getCacheEntry()?.data?.data.id;
              if (ev.data && ev.event === EventKeysEnum.Notification) {
                const item = JSON.parse(ev.data).data;

                if (
                  item.target === TARGET_NOTIFICATION.ALL ||
                  item.creator_id === userId
                ) {
                  return dispatch(setNewNotification(JSON.parse(ev.data).data));
                }
              }
              if (ev.data && ev.event === EventKeysEnum.OpenOrder) {
                if (userId === JSON.parse(ev.data).data.user_id) return;
                return dispatch(onOrderOpen(JSON.parse(ev.data).data));
              }
              if (ev.data && ev.event === EventKeysEnum.CloseOrder) {
                if (userId === JSON.parse(ev.data).data.user_id) return;
                return dispatch(onOrderClose(JSON.parse(ev.data).data));
              }
              if (ev.data && ev.event === EventKeysEnum.Counterparty) {
                const item = JSON.parse(ev.data).data as {
                  counterparty_id: number;
                  balance: number;
                  nova_poshta: boolean;
                };
                const moduleName = item.nova_poshta
                  ? 'getNPModule'
                  : 'getUPModule';
                const state: any = getState();
                const queries = Object.entries(state.goModules.queries) as [
                  string,
                  TypedUseQueryStateResult<any, any, any>,
                ][];
                const cashArgs = queries.reduce<string[]>(
                  (accumulator, currentValue) => {
                    if (currentValue[0].startsWith(moduleName)) {
                      return [...accumulator, currentValue[1].originalArgs];
                    }
                    return accumulator;
                  },
                  []
                );
                cashArgs.forEach((args) => {
                  const update = goModulesSlice.util.updateQueryData(
                    moduleName,
                    args,
                    (draft) => {
                      if (draft?.data?.map) {
                        draft.data = draft.data.map((draftCounterparty) => {
                          if (draftCounterparty.id === item.counterparty_id) {
                            return {
                              ...draftCounterparty,
                              balance: item.balance,
                            } as any;
                          } else return draftCounterparty;
                        });
                      }
                    }
                  );
                  dispatch(update as ThunkAction<any, any, any, any>);
                });
              }
            },
            onclose() {
              throw new RetriableError();
            },
            onerror(err) {
              // temporary to
              console.log('SSE onerror', err);
              if (err instanceof FatalError) {
                throw err;
              }
            },
            openWhenHidden: true,
          }
        );
      },
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetUserQuery,
  useGetNotificationsQuery,
  useLazyGetUsersQuery,
} = goUsers;
